import { createContext, useContext, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { Button, Card, ScrollArea, Typography } from '../ui';
import styles from './CategoryView.module.css';

// The one layout every category page shares (Figma 21055:246799 "[Home 3.0] Categories"), on the home's
// category view AND on the standalone screens. Shared 1:1 with iOS `CategorySection` and Android
// `CategorySection`:
//
//   CategoryHeader   title + description, the filter dropdowns at the trailing edge (stacked under the
//                    text on a narrow pane). A standalone screen passes no title: its PageHeader already
//                    names the page, so only the filters are drawn.
//   CategoryToolbar  one white card holding the tabs (or a label) and, at its end, the extra controls and
//                    the create button. On a narrow pane it drops the card and becomes an underline strip.
//   CategoryList     one white card the rows sit in, parted by hairlines. On a narrow pane it drops the
//                    card and every row becomes its own card again (the mobile frames).
//
// ⚠️ Written-down divergence: the phones put the create action into the home's sticky composer instead of
// a button. Web's home has no composer (only the floating chat bubble), so web keeps the button at every
// width.

/** Inside a CategoryList, a row's own Card drops its surface so the list's card is the only one. */
const JoinedListContext = createContext(false);

/** The class a row's own surface takes inside a CategoryList; '' anywhere else. */
export function useCategoryRowClass(): string {
  return useContext(JoinedListContext) ? styles.joinedRow : '';
}

/** True inside a CategoryList — for a row whose layout changes there (an origin line becomes a header row). */
export function useInCategoryList(): boolean {
  return useContext(JoinedListContext);
}

export function CategoryHeader({ title, description, filters }: { title?: string; description?: ReactNode; filters?: ReactNode }) {
  if (!title && !filters) return null;
  return (
    <div className={`${styles.header}${title ? '' : ` ${styles.headerFiltersOnly}`}`}>
      {title && (
        <div className={styles.headerText}>
          <h2 className={styles.title}>{title}</h2>
          {description && (
            <Typography variant="heading-04" color="secondary" component="p">
              {description}
            </Typography>
          )}
        </div>
      )}
      {filters && <div className={styles.filters}>{filters}</div>}
    </div>
  );
}

/** "× Clear filters · N" — drawn by the filter row only while something is narrowed. */
export function ClearFiltersButton({ count, onClear }: { count: number; onClear: () => void }) {
  const { t } = useTranslation();
  if (count <= 0) return null;
  return (
    <Button variant="text" size="small" startIcon={<X size={16} aria-hidden />} onClick={onClear}>
      {t('categoryView.clearFilters', { count })}
    </Button>
  );
}

export function CategoryToolbar({ children, end, action }: { children?: ReactNode; end?: ReactNode; action?: ReactNode }) {
  return (
    <Card size="sm" disablePadding className={styles.toolbar}>
      {/* Four task tabs outrun a phone's width, so the strip scrolls sideways rather than wrapping. */}
      <ScrollArea axis="horizontal" bar="hidden" className={styles.toolbarStart}>
        {children}
      </ScrollArea>
      {(end || action) && (
        <div className={styles.toolbarEnd}>
          {end}
          {action && <span className={styles.action}>{action}</span>}
        </div>
      )}
    </Card>
  );
}

/** A plain label in the toolbar's start slot, for a page with no tabs ("All Goals"). */
export function CategoryToolbarLabel({ children }: { children: ReactNode }) {
  return <span className={styles.toolbarLabel}>{children}</span>;
}

export function CategoryList({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <JoinedListContext.Provider value>
      <Card size="sm" disablePadding className={`${styles.list}${className ? ` ${className}` : ''}`}>
        {children}
      </Card>
    </JoinedListContext.Provider>
  );
}

/** A group's header row inside a CategoryList — "In Progress · 2", "Gmail · 2", "From goal …". */
export function CategoryListGroupHeader({ icon, children, level = 3 }: { icon?: ReactNode; children: ReactNode; level?: number }) {
  // A div that reads as a heading, so screen-reader heading navigation still walks the groups.
  return (
    <div className={styles.groupHeader} role="heading" aria-level={level}>
      {icon && <span className={styles.groupIcon}>{icon}</span>}
      <span className={styles.groupTitle}>{children}</span>
    </div>
  );
}

/** A section title between lists on the page itself — "Suggested goals · 3". */
export function CategorySectionTitle({ children, end }: { children: ReactNode; end?: ReactNode }) {
  return (
    <div className={styles.sectionTitleRow}>
      <h3 className={styles.sectionTitle}>{children}</h3>
      {end}
    </div>
  );
}

/** The page's vertical rhythm: header → 32 → toolbar → 16 → lists. */
export function CategoryPage({ children }: { children: ReactNode }) {
  return <div className={styles.page}>{children}</div>;
}
